<div #scrollContainer class="ai-agent-messages">
  @if (loading()) {
    <div class="ai-agent-messages__loading">
      <sim-spinner [diameter]="32" [strokeWidth]="3" />
    </div>
  } @else if (!messages().length) {
    <div class="ai-agent-messages__empty">
      <div class="ai-agent-messages__intro">
        <span class="ai-agent-messages__intro-icon">
          <sim-icon icon="magic_pen" [sizeInPx]="28" />
        </span>
        <h1 class="ai-agent-messages__intro-title" i18n="@@AI_AGENT_EMPTY_TITLE">What can I help with?</h1>
        <p class="ai-agent-messages__intro-text" i18n="@@AI_AGENT_EMPTY_SCOPE">
          Ask about spaces, files, assets, tasks and events in
          <strong class="ai-agent-messages__intro-space">{{ spaceName() }}</strong
          >. I can also prepare a new asset, task or event for you to save.
        </p>
      </div>
      <div class="ai-agent-messages__examples">
        <span class="ai-agent-messages__examples-label" i18n="@@AI_AGENT_EXAMPLES_LABEL">For example</span>
        <!-- Plain text, not buttons: the user types the question. -->
        <p class="ai-agent-messages__examples-text">
          <ng-container i18n="@@AI_AGENT_EXAMPLE_OVERDUE">“Which tasks are overdue?”</ng-container><br />
          <ng-container i18n="@@AI_AGENT_EXAMPLE_CREATE_TASK"
            >“Create a task to replace the extinguisher in Room 203 by Friday”</ng-container
          ><br />
          <ng-container i18n="@@AI_AGENT_EXAMPLE_ROOM">“What's in Room 56?”</ng-container>
        </p>
      </div>
    </div>
  } @else {
    @for (message of messages(); track message.id) {
      <div
        class="ai-agent-messages__item"
        [class.ai-agent-messages__item--user]="message.role === AgentMessageRole.User"
        [class.ai-agent-messages__item--assistant]="message.role !== AgentMessageRole.User"
        [class.ai-agent-messages__item--pending]="message.status === 'pending'"
        [class.ai-agent-messages__item--failed]="message.status === 'failed'"
      >
        @if (message.role === AgentMessageRole.Assistant) {
          <div
            class="ai-agent-messages__bubble ai-agent-messages__bubble--markdown"
            [innerHTML]="message.content | agentMarkdown"
          ></div>
        } @else {
          <div class="ai-agent-messages__bubble">{{ message.content }}</div>
        }
        @if (message.actions.length) {
          <ul class="ai-agent-messages__cards">
            @for (action of message.actions; track action.id) {
              <li
                class="ai-agent-card"
                [class.ai-agent-card--draft]="action.kind !== 'navigation' && !action.completed"
                [class.ai-agent-card--done]="action.completed"
              >
                <div class="ai-agent-card__head">
                  <span class="ai-agent-card__icon">
                    <sim-icon [icon]="action.completed ? 'task_alt' : actionIcon(action)" [sizeInPx]="18" />
                  </span>
                  <div class="ai-agent-card__texts">
                    @if (eyebrow(action); as eyebrowText) {
                      <span class="ai-agent-card__eyebrow" simEllipsisTooltip>{{ eyebrowText }}</span>
                    }
                    <span class="ai-agent-card__title" simEllipsisTooltip>{{ action.label }}</span>
                    @if (action.helper) {
                      <span class="ai-agent-card__helper" simEllipsisTooltip>
                        {{ action.helper }}
                        @if (action.completed && action.kind === 'status' && action.completedAt) {
                          · {{ action.completedAt | date: 'shortTime' }}
                        }
                      </span>
                    }
                    @if (action.pills.length) {
                      <div class="ai-agent-card__pills">
                        @for (pill of action.pills; track $index) {
                          <span class="ai-agent-card__pill" [class]="'ai-agent-card__pill--' + pill.tone">{{
                            pill.label
                          }}</span>
                        }
                      </div>
                    }
                  </div>
                </div>

                @if (!action.completed) {
                  @if (action.changes.length) {
                    <ul class="ai-agent-card__changes">
                      @for (change of action.changes; track $index) {
                        <li class="ai-agent-card__change">
                          <span class="ai-agent-card__change-label" simEllipsisTooltip>{{ change.label }}</span>
                          @if (change.oldValue) {
                            <span class="ai-agent-card__change-old" simEllipsisTooltip>{{ change.oldValue }}</span>
                            <span class="ai-agent-card__change-arrow" aria-hidden="true">→</span>
                          }
                          <span class="ai-agent-card__change-new" simEllipsisTooltip>{{
                            change.newValue ?? removedLabel
                          }}</span>
                        </li>
                      }
                    </ul>
                  }
                  @if (action.duplicates.length) {
                    <div class="ai-agent-card__duplicate">
                      <sim-icon class="ai-agent-card__duplicate-icon" icon="error_outline" [sizeInPx]="16" />
                      @if (duplicateRoom(action); as room) {
                        <span i18n="@@AI_AGENT_POSSIBLE_DUPLICATE_IN_ROOM"
                          >Possible duplicate:
                          <strong class="ai-agent-card__duplicate-names">{{ action.duplicates.join(', ') }}</strong>
                          already exists in {{ room }}.</span
                        >
                      } @else {
                        <span i18n="@@AI_AGENT_POSSIBLE_DUPLICATE"
                          >Possible duplicate:
                          <strong class="ai-agent-card__duplicate-names">{{ action.duplicates.join(', ') }}</strong
                          >.</span
                        >
                      }
                    </div>
                  }
                  <div class="ai-agent-card__buttons">
                    <button
                      type="button"
                      class="ai-agent-card__button"
                      [class.ai-agent-card__button--primary]="action.kind !== 'navigation'"
                      (click)="onAction(action)"
                    >
                      {{ actionOpenLabel(action) }}
                    </button>
                    @if (action.canGoToPosition) {
                      <button
                        type="button"
                        class="ai-agent-card__button ai-agent-card__button--secondary"
                        (click)="onGoToPosition(action)"
                      >
                        <sim-icon icon="location_on" [sizeInPx]="16" />
                        <ng-container i18n="@@AI_AGENT_GO_TO_3D">Go to 3D</ng-container>
                      </button>
                    }
                  </div>
                }
              </li>
            }
          </ul>
          @if (hasDraft(message)) {
            <p class="ai-agent-messages__footnote" i18n="@@AI_AGENT_NOTHING_SAVED">
              Nothing is saved until you confirm it in the form.
            </p>
          }
        }
        @if (message.status === 'failed') {
          <div class="ai-agent-messages__meta ai-agent-messages__meta--failed">
            <sim-icon icon="error_outline" [sizeInPx]="14" />
            <span i18n="@@AI_AGENT_NOT_SENT">Not sent</span>
            <button
              type="button"
              class="ai-agent-messages__retry"
              [attr.aria-label]="retryLabel"
              (click)="onRetry(message)"
            >
              <sim-icon icon="replay" [sizeInPx]="13" />
              <ng-container i18n="@@AI_AGENT_RETRY">Retry</ng-container>
            </button>
          </div>
        } @else if (showUsage && message.usage) {
          <div class="ai-agent-messages__meta">
            {{ message.usage.totalTokens }} <ng-container i18n="@@AI_AGENT_USAGE_TOKENS">tokens</ng-container>
          </div>
        }
      </div>
    }
  }
  @if (cardsHint() && hasCards()) {
    <p class="ai-agent-messages__footnote">{{ cardsHint() }}</p>
  }
  @if (sending()) {
    <div class="ai-agent-messages__thinking" role="status">
      <span class="ai-agent-messages__thinking-dots" aria-hidden="true">
        <span class="ai-agent-messages__thinking-dot"></span>
        <span class="ai-agent-messages__thinking-dot"></span>
        <span class="ai-agent-messages__thinking-dot"></span>
      </span>
      <span class="ai-agent-messages__thinking-text">
        <span i18n="@@AI_AGENT_THINKING">Assistant is thinking…</span>
        <span class="ai-agent-messages__thinking-hint" i18n="@@AI_AGENT_THINKING_HINT"
          >this can take a few seconds</span
        >
      </span>
    </div>
  }
</div>
